
import { getNodeIdAtPointInCanvas } from "@/lib/canvas/drag/element-drag";
import {
  getTreeNodePositionAndSize,
  getNodeElementWrapperStyles,
  getDomElementFromTreeNode
} from "@/lib/canvas/helpers";
import { findNode } from "@/lib/tree/tree";
import { documentStore, uiStore } from "@/store/store";
import { Component, Vue } from "vue-property-decorator";
import throttle from "lodash/throttle";
import { forceNextTick } from "@/util/force-next-tick";
import AddSectionButton from "./components/AddSectionButton.vue";
import AddAnotherButton from "./components/AddAnotherButton.vue";
import EditImageButton from "./components/EditImageButton.vue";
import EditBackgroundImageButton from "./components/EditBackgroundImageButton.vue";
import EditIconButton from "./components/EditIconButton.vue";
import EditLinkButton from "./components/EditLinkButton.vue";
import EditContentPropertiesButton from "./components/EditContentPropertiesButton.vue";
import {
  GRID_SLUG,
  IMAGE_SLUG,
  SECTION_SLUG,
  ICON_SLUG
} from "@/lib/elements/constants";
import { isChildOfLinkElement, isLinkElement } from "@/futurelayer/links/links";
import lodash from "lodash";

@Component({
  components: {
    AddSectionButton,
    AddAnotherButton,
    EditImageButton,
    EditBackgroundImageButton,
    EditLinkButton,
    EditIconButton,
    EditContentPropertiesButton
  }
})
export default class NodeSingularityHover extends Vue {
  interval!: NodeJS.Timer;
  refreshRate = 500;
  shouldShowControls = false;

  unsubscribeFromStorePositionWrapper = () => { };

  throttledNextTickPositionWrapper = throttle(
    () => forceNextTick(() => this.positionWrapper()),
    100
  );

  beforeMount() {
    this.positionWrapper();

    this.unsubscribeFromStorePositionWrapper = this.$store.subscribe(
      this.throttledNextTickPositionWrapper
    );

    window.addEventListener("resize", this.throttledNextTickPositionWrapper);
  }

  mounted() {
    const canvasElement =
      window.Breakdance.canvas.document.getElementById("breakdance_canvas");

    if (!canvasElement) return;

    canvasElement.addEventListener(
      "mousemove",
      this.throttledShowHoverControlsOnMouseMoveCanvas
    );
    canvasElement.addEventListener(
      "mouseleave",
      this.disableMouseHoverOnMouseLeaveCanvas
    );

    this.interval = setInterval(
      this.throttledNextTickPositionWrapper,
      this.refreshRate
    );
  }

  beforeDestroy() {
    const canvasElement =
      window.Breakdance.canvas.document.getElementById("breakdance_canvas");

    this.unsubscribeFromStorePositionWrapper();
    window.removeEventListener("resize", this.throttledNextTickPositionWrapper);
    canvasElement?.removeEventListener(
      "mousemove",
      this.throttledShowHoverControlsOnMouseMoveCanvas
    );
    clearInterval(this.interval);
  }

  get wrapperClassList() {
    return {
      "node-hover-controls-show": this.shouldShowControls && this.isNotDragging,
      "node-hover-controls--outline": this.isNotDragging
    };
  }

  get isNotDragging() {
    return !uiStore.isCanvasElementBeingDragged && !uiStore.isDragMixinDragging;
  }

  get element() {
    if (
      !uiStore.lastHoveredOnCanvasElementId ||
      uiStore.elementWithContentEditable
    )
      return null;

    const lastHoveredElement = findNode(
      documentStore.document.tree,
      uiStore.lastHoveredOnCanvasElementId
    );

    return lastHoveredElement || null;
  }

  setHoveredNode(x: number, y: number) {
    const hoveredNodeId = getNodeIdAtPointInCanvas(x, y);

    if (hoveredNodeId !== undefined) {
      const hoveredNode = findNode(documentStore.document.tree, hoveredNodeId);

      if (hoveredNode) {
        uiStore.setLastHoveredOnCanvasElementId(hoveredNode.id);
      }
    }
  }

  disableMouseHoverOnMouseLeaveCanvas = () => {
    // without a timeout, the mousemove event will trigger after mouseleave
    // setting the hoveredElement again
    setTimeout(() => uiStore.setLastHoveredOnCanvasElementId(null), 110);
  };

  throttledShowHoverControlsOnMouseMoveCanvas = throttle(
    this.showHoverControlsOnMouseMoveCanvas,
    100
  );
  showHoverControlsOnMouseMoveCanvas(e: MouseEvent) {
    const { clientX: mouseX, clientY: mouseY } = e;

    this.setHoveredNode(mouseX, mouseY);

    if (!this.element) return;

    const elementPositionAndSize = getTreeNodePositionAndSize(
      this.element,
      "absolute"
    );

    if (!elementPositionAndSize) return;

    const {
      x: elementX,
      y: elementY,
      width: elementWidth,
      height: elementHeight
    } = elementPositionAndSize;

    this.shouldShowControls =
      mouseX > elementX + elementWidth - 48 &&
      mouseX < elementX + elementWidth &&
      mouseY > elementY &&
      mouseY < elementY + elementHeight;
  }

  wrapperPositionStyle: { [key: string]: string } = {
    display: "none"
  };

  positionWrapper() {
    if (uiStore.elementWithContentEditable || !this.element) {
      this.wrapperPositionStyle = {
        display: "none"
      };

      return;
    }

    this.wrapperPositionStyle = getNodeElementWrapperStyles(
      getDomElementFromTreeNode(this.element),
      4
    );
  }

  get isSectionElement() {
    return this.element && this.element.data.type === SECTION_SLUG;
  }

  get isImageElement() {
    // lack of suport for legacy images (LEGACY_IMAGE_SLUG) is deliberate
    return this.element && this.element.data.type === IMAGE_SLUG;
  }

  get isSectionElementWithBackgroundImage() {
    return (
      this.element &&
      this.element.data.type === SECTION_SLUG &&
      lodash.get(this.element.data.properties, "design.background.type") ==
      "image"
    );
  }

  get isIconElement() {
    return this.element && this.element.data.type === ICON_SLUG;
  }

  get isChildOfGrid() {
    if (!this.element) return false;

    const parent = findNode(
      documentStore.document.tree,
      this.element._parentId
    );

    if (!parent) return false;

    return parent.data.type === GRID_SLUG;
  }

  get isLinkElementOrChildOfLinkElement() {
    if (!this.element) return false;
    return (
      isLinkElement(this.element.data.type) ||
      isChildOfLinkElement(this.element)
    );
  }
}
